import clsx from 'clsx';
import { useGetPhonicsSoundsQuery, PhonicsActivity } from '@/api/types';
import { Table } from '../Table';
import { GreenBox } from '../GreenBox';
import { useMemo } from 'react';
import { truncate } from '@/utils/strings';
import { findSoundsInString } from '@/utils/sounds';

type ActivityListViewType = 'list' | 'grid';

interface AcivityPodProps {
  activity: PhonicsActivity;
  selected?: boolean;
  inverted?: boolean;
  onSelect: (activity: PhonicsActivity | undefined) => void;
}

export const ActivityPod = ({
  activity,
  selected = false,
  inverted = false,
  onSelect,
}: AcivityPodProps) => {
  const { data: soundsResult } = useGetPhonicsSoundsQuery();

  const sounds = useMemo(
    () =>
      findSoundsInString(
        soundsResult?.getPhonicsSounds.map((sound) => sound.name) || [],
        activity.sounds || '',
      ),
    [activity, soundsResult],
  );

  return (
    <GreenBox
      size="lg"
      bg={inverted ? 'wt' : selected ? 'dk' : 'md'}
      onClick={() => onSelect(selected ? undefined : activity)}
      smallText
      highlighted={selected}
    >
      <div className="relative w-full h-full overflow-hidden p-2 pr-6 flex flex-col justify-between text-left">
        <h3
          className="text-4xl font-sassoonInfant break-words"
          title={sounds.join('')}
        >
          {truncate(sounds.join(''))}
        </h3>

        <div className="underline hover:no-underline">{activity.type.name}</div>
      </div>
    </GreenBox>
  );
};

interface ManageActivityListProps {
  viewAs: ActivityListViewType;
  activities: Array<PhonicsActivity>;
  selectedActivity: PhonicsActivity | undefined;
  onSelectActivity: (activity: PhonicsActivity | undefined) => void;
  inverted?: boolean;
}

export const ManageActivityList = ({
  viewAs,
  activities,
  selectedActivity,
  onSelectActivity,
  inverted = false,
}: ManageActivityListProps) => (
  <>
    {viewAs === 'list' && (
      <Table
        rows={activities}
        columns={{
          name: {
            label: 'Name',
            render: (columnName, activity) => (
              <button
                className={clsx(
                  'text-green-darker hover:text-green-light',
                  activity === selectedActivity && 'font-bold',
                )}
                onClick={() =>
                  onSelectActivity(
                    activity === selectedActivity ? undefined : activity,
                  )
                }
              >
                {activity.type.name}
              </button>
            ),
          },
          sounds: {
            label: 'Sounds',
          },
          words: {
            label: 'Words',
            render: (columnName, activity) =>
              Array.isArray(activity.words) ? activity.words.join(', ') : null,
          },
          sentences: {
            label: 'Sentences',
            render: (columnName, activity) =>
              Array.isArray(activity.sentences)
                ? activity.sentences.map((sentence, index) => (
                    <>
                      {sentence}
                      {activity.sentences &&
                        index !== activity.sentences.length - 1 && <br />}
                    </>
                  ))
                : null,
          },
        }}
        striped
      />
    )}

    {viewAs === 'grid' && (
      <div className="flex flex-wrap gap-3">
        {activities.map((activity) => (
          <ActivityPod
            key={activity.id}
            activity={activity}
            selected={activity === selectedActivity}
            onSelect={onSelectActivity}
            inverted={inverted}
          />
        ))}
      </div>
    )}
  </>
);
